/**
 * ReviewThreadView — Threaded renderer for an App Store review's interaction
 * chain.
 *
 * App Store reviews are stored as `Interaction` records that share a
 * `thread_id` (the ASC review ID) with the developer's outbound reply and any
 * subsequent customer reply-to-reply. This component fetches that chain via the
 * canonical GET /v1/interactions/{id}/thread endpoint (the same one the inbox
 * conversation view uses) and renders it with the shared `MessageBubble` so the
 * operator sees an identical thread experience across email and reviews.
 *
 * It owns its own fetch + loading/error state (unlike the inbox
 * `ConversationView`, which reads from the inbox store) so it can be dropped
 * into both the ReviewInspector "Thread" tab and the standalone pop-out window
 * without depending on either feature's store.
 */

import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { fetchThreadMessages } from '@/features/inbox-manager/api'
import { MessageBubble } from '@/features/inbox-manager/components/MessageBubble'
import type { Message } from '@/features/inbox-manager/types'

interface ReviewThreadViewProps {
  /** Interaction id of the review (or any interaction within the thread). */
  interactionId: string
}

export function ReviewThreadView({ interactionId }: ReviewThreadViewProps): ReactNode {
  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    fetchThreadMessages(interactionId)
      .then((msgs) => {
        if (cancelled) return
        setMessages(msgs)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : String(err))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [interactionId])

  if (loading && messages.length === 0) {
    return (
      <div style={styles.loadingContainer}>
        <Skeleton style={{ width: '70%', height: '90px', alignSelf: 'flex-start' }} />
        <Skeleton style={{ width: '60%', height: '70px', alignSelf: 'flex-end' }} />
      </div>
    )
  }

  if (error && messages.length === 0) {
    return (
      <div style={styles.errorContainer}>
        <p style={styles.errorText}>Failed to load review thread: {error}</p>
      </div>
    )
  }

  if (messages.length === 0) {
    return (
      <div style={styles.emptyContainer}>
        <p style={styles.emptyText}>No thread for this review yet.</p>
        <p style={styles.emptySubtext}>
          The original review is shown here. A developer reply (and any customer
          follow-up) will appear once it is posted.
        </p>
      </div>
    )
  }

  // Chronological order (oldest first) so the original review sits at the top
  // and the reply chain reads top-to-bottom.
  const sorted = [...messages].sort((a, b) => a.received_at - b.received_at)

  return (
    <div style={styles.container} role="log" aria-label="Review thread" aria-live="polite">
      {sorted.map((message, idx) => (
        <MessageBubble key={message.id ?? idx} message={message} />
      ))}
    </div>
  )
}

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '16px',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '16px',
  },
  errorContainer: {
    padding: '20px',
  },
  errorText: {
    margin: 0,
    fontSize: '13px',
    color: 'var(--error)',
  },
  emptyContainer: {
    padding: '24px 20px',
    textAlign: 'center',
  },
  emptyText: {
    margin: 0,
    fontSize: '14px',
    fontWeight: 500,
    color: 'var(--text-secondary)',
  },
  emptySubtext: {
    marginTop: '6px',
    fontSize: '12px',
    color: 'var(--text-muted)',
  },
}

export default ReviewThreadView
